import React from "react";
import { convertGraphQLErrorsToSubmissionErrors } from "@hex-insights/app-modules";
import { Heading, Paragraph, Row } from "@hex-insights/core";
import {
	Form,
	FormSubmitFunction,
	FormType,
	submissionFailure,
	submissionSuccess,
	SubmitButton,
	useFormState,
	useOnSuccess,
} from "@hex-insights/forms";
import { useActivePageRegistration, useHistory } from "@hex-insights/router";
import {
	ButtonLink,
	Icon,
	OrderDirection,
	Student,
	StudentDataConsentFormValues,
	StudentDataConsentListQuery,
	StudentDataConsentOrderField,
	StudentDataConsentStudentForm,
	useCreateOrUpdateRecentStudentDataConsentsMutation,
	useStudentDataConsentListQuery,
	useStudentNamesQuery,
} from "@hex-insights/verita-romania.shared";
import { ReEnrollmentLayout } from "../Components";
import { reEnrollmentMealPlansPageInfo } from "../MealPlansPage/pageinfo";
import { reEnrollmentStudentHealthPageInfo } from "../StudentHealthPage/pageinfo";
import { getReEnrollmentStudentFilter } from "../Utilities";
import { reEnrollmentDataConsentPageInfo } from "./pageinfo";
import generalStyles from "../general.module.css";

export function ReEnrollmentDataConsentPage() {
	useActivePageRegistration(reEnrollmentDataConsentPageInfo);

	const { data } = useStudentDataConsentListQuery({
		variables: {
			filters: {
				student: [getReEnrollmentStudentFilter()],
			},
			order: { field: StudentDataConsentOrderField.CreatedAt, direction: OrderDirection.Desc },
			pagination: { first: 1 },
		},
		fetchPolicy: "network-only",
	});
	const { data: studentData } = useStudentNamesQuery({
		variables: {
			filters: getReEnrollmentStudentFilter(),
		},
		fetchPolicy: "network-only",
	});

	const studentIDs = React.useMemo(() => {
		return studentData?.studentConnection.edges.map((edge) => edge.node.id) ?? [];
	}, [studentData]);

	if (!data) {
		return (
			<ReEnrollmentLayout>
				<TopContent />

				<div>Loading...</div>
			</ReEnrollmentLayout>
		);
	}

	const lastStudentDataConsent = data?.studentDataConsentConnection.edges[0]?.node ?? null;

	return <LoadedContent lastStudentDataConsent={lastStudentDataConsent} studentIDs={studentIDs} />;
}

type DataConsentFormValues = Pick<
	StudentDataConsentFormValues.Create,
	| "allowPublishVisualMediaOfStudentGeneral"
	| "allowPublishVisualMediaOfStudentForPromotion"
	| "allowPublishVisualMediaOfGuardianForPromotion"
	| "allowPublishStudentWorkGeneral"
	| "allowShareInformationWithPearsonEdexcel"
	| "allowShareInformationWithIbOrganization"
	| "allowShareInformationWithOnlinePlatforms"
	| "allowBirthdayInCalendar"
	| "allowSendNewsletters"
	| "allowSendCalendarEvents"
>;

type LoadedContentProps = {
	lastStudentDataConsent: StudentDataConsentListQuery["studentDataConsentConnection"]["edges"][0]["node"] | null;
	studentIDs: Student["id"][];
};

function LoadedContent({ lastStudentDataConsent, studentIDs }: LoadedContentProps) {
	const initialFormValues = React.useMemo<DataConsentFormValues>(() => {
		return {
			allowPublishVisualMediaOfStudentGeneral: lastStudentDataConsent?.allowPublishVisualMediaOfStudentGeneral ?? true,
			allowPublishVisualMediaOfStudentForPromotion:
				lastStudentDataConsent?.allowPublishVisualMediaOfStudentForPromotion ?? true,
			allowPublishVisualMediaOfGuardianForPromotion:
				lastStudentDataConsent?.allowPublishVisualMediaOfGuardianForPromotion ?? true,
			allowPublishStudentWorkGeneral: lastStudentDataConsent?.allowPublishStudentWorkGeneral ?? true,
			allowShareInformationWithPearsonEdexcel: lastStudentDataConsent?.allowShareInformationWithPearsonEdexcel ?? true,
			allowShareInformationWithIbOrganization: lastStudentDataConsent?.allowShareInformationWithIbOrganization ?? true,
			allowShareInformationWithOnlinePlatforms:
				lastStudentDataConsent?.allowShareInformationWithOnlinePlatforms ?? true,
			allowBirthdayInCalendar: lastStudentDataConsent?.allowBirthdayInCalendar ?? true,
			allowSendNewsletters: lastStudentDataConsent?.allowSendNewsletters ?? true,
			allowSendCalendarEvents: lastStudentDataConsent?.allowSendCalendarEvents ?? true,
		};
	}, [lastStudentDataConsent]);

	const formState = useFormState({
		initialFormValues,
		formType: FormType.Create,
	});

	const [createOrUpdate] = useCreateOrUpdateRecentStudentDataConsentsMutation();
	const onSubmit = React.useCallback<FormSubmitFunction<DataConsentFormValues>>(
		async ({ formValues }) => {
			const { errors } = await createOrUpdate({
				variables: {
					studentDataConsentInput: { ...formValues, studentID: "0" }, // studentID ignored by server
					studentIDs,
				},
			});
			if (errors) {
				return submissionFailure(convertGraphQLErrorsToSubmissionErrors(errors));
			}
			return submissionSuccess();
		},
		[createOrUpdate, studentIDs],
	);

	const history = useHistory();
	useOnSuccess(formState, () => history.push(reEnrollmentMealPlansPageInfo.to));

	return (
		<ReEnrollmentLayout
			footerContent={
				<Row justify="space-between">
					<ButtonLink variant="link" to={reEnrollmentStudentHealthPageInfo.to}>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<Icon.ArrowLeft size={24} /> Back
						</Row>
					</ButtonLink>

					<SubmitButton onClick={formState.onSubmitWrapper(onSubmit)} submissionStatus={formState.submissionStatus}>
						Save
					</SubmitButton>
				</Row>
			}
		>
			<TopContent />

			<Form
				formState={formState}
				onSubmit={formState.onSubmitWrapper(onSubmit)}
				name="reEnrollment.dataConsent"
				noNotifications
				noNavigationPrompt
				mergeUnsavedChangesAutomatically
			>
				<StudentDataConsentStudentForm.QuestionFields formState={formState} />
			</Form>
		</ReEnrollmentLayout>
	);
}

function TopContent() {
	return (
		<React.Fragment>
			<Heading.H1 className={generalStyles["heading"]}>
				General Data Protection Regulation (GDPR) Consent Form
			</Heading.H1>

			<Paragraph>Please read through each item carefully.</Paragraph>
		</React.Fragment>
	);
}
